import { z } from "zod"

/**
 * Branded (nominal) types for VidMax.
 *
 * A branded type is a primitive (`string` / `number`) tagged with a phantom
 * property so the compiler treats it as a distinct type. It prevents an entire
 * class of "right primitive, wrong meaning" bugs that the structural type system
 * otherwise waves through — e.g. passing a `userId` where a `teamId` is expected,
 * charging `cents` as `dollars`, or persisting a raw plaintext token where a hash
 * belongs.
 *
 * Rules:
 * - A plain `string`/`number` is NOT assignable to a brand — you must go through a
 *   constructor (`asTeamId`, `asCents`, …). That cast IS the audit checkpoint: it's
 *   the one place you assert "this value really is a TeamId".
 * - A brand IS assignable back to its base primitive, so branded values flow into
 *   existing `string`/`number` APIs without friction.
 * - Two brands over the same base are mutually incompatible (TeamId ≠ UserId).
 *
 * Import directly (not via the barrel) to avoid pulling the whole package:
 *   import { type TeamId, asTeamId, centsToDollars } from "@aix/vidmax-shared/src/types/branded"
 */

export type Brand<T, B extends string> = T & { readonly __brand: B }

interface EntityFkBrands {
  teamId: TeamId
  userId: UserId
  videoId: VideoId
  seriesId: SeriesId
  assetId: AssetId
  feedbackId: FeedbackId
  postedMediaId: PostedMediaId
  postedMediaMetricSnapshotId: PostedMediaMetricSnapshotId
  postedMediaSyncRunId: PostedMediaSyncRunId
  scheduledPostId: ScheduledPostId
  productId: ProductId
  albumId: AlbumId
  audioAssetId: AssetId
  primaryAssetId: AssetId
  parentAlbumId: AlbumId
  assetJobId: AssetJobId
  jobId: AssetJobId
  parentAssetId: AssetId
  coverAssetId: AssetId
  backgroundMusicId: BackgroundMusicId
  vm_BackgroundMusicId: BackgroundMusicId
  vm_VoiceId: VoiceId
  createdBy: UserId
  createdById: UserId
  brandId: BrandId
  contentBrandId: ContentBrandId
  avatarId: AvatarId
  voiceId: VoiceId
  sceneId: SceneId
  uploadedAudioId: UploadedAudioId
  linkId: ShareableLinkToken
  contentSeedId: ContentSeedId
  endpointId: WebhookEndpointId
  notificationId: NotificationId
  parentId: EditorVersionId
  parentSnapshotId: StoryboardSnapshotId
  snapshotId: StoryboardSnapshotId
  workflowId: WorkflowId
  workflowExecutionId: WorkflowExecutionId
  executionId: WorkflowExecutionId
  reviewItemId: WorkflowReviewItemId
  scratchId: ScratchId
  boardId: MediaSpaceBoardId
  boardAssetId: MediaSpaceBoardAssetId
  commentId: AssetCommentId
  parentCommentId: AssetCommentId
  columnId: MediaSpaceBoardColumnId
  fromColumnId: MediaSpaceBoardColumnId
  toColumnId: MediaSpaceBoardColumnId
  automationId: MediaSpaceAutomationId
  ownerId: UserId
  movedById: UserId
  reviewedById: UserId
  triggeredById: UserId
  carouselId: ImageCarouselId
  imageCarouselId: ImageCarouselId
  imageCarouselVersionId: ImageCarouselVersionId
}

export type RebrandFks<T> = {
  [K in keyof T]: K extends keyof EntityFkBrands
    ? T[K] extends string | null
      ? Extract<T[K], null> | EntityFkBrands[K]
      : T[K]
    : T[K]
}

export type Entity<Base, Id> = RebrandFks<Omit<Base, "id"> & { id: Id }>

// ---------------------------------------------------------------------------
// #1 Tenant-isolation IDs — transposing these = cross-tenant access / auth bypass
// ---------------------------------------------------------------------------

export type TeamId = Brand<string, "TeamId">
export type UserId = Brand<string, "UserId">

export const asTeamId = (id: string): TeamId => id as TeamId
export const asUserId = (id: string): UserId => id as UserId

export const zTeamId = z.string().min(1).transform(asTeamId)
export const zUserId = z.string().min(1).transform(asUserId)

// ---------------------------------------------------------------------------
// Entity IDs — all bare cuids today, so the compiler can't catch a videoId
// passed where an assetId belongs. Brand them so a transposed id is a type error.
// ---------------------------------------------------------------------------

export type VideoId = Brand<string, "VideoId">
export type SeriesId = Brand<string, "SeriesId">
export type AssetId = Brand<string, "AssetId">
export type AudioAssetId = Brand<string, "AudioAssetId">
export type FeedbackId = Brand<string, "FeedbackId">
export type PostedMediaId = Brand<string, "PostedMediaId">
export type PostedMediaMetricSnapshotId = Brand<string, "PostedMediaMetricSnapshotId">
export type PostedMediaSyncRunId = Brand<string, "PostedMediaSyncRunId">
export type ScheduledPostId = Brand<string, "ScheduledPostId">
export type ProductId = Brand<string, "ProductId">
export type AlbumId = Brand<string, "AlbumId">
export type BrollBatchId = Brand<string, "BrollBatchId">
export type BrollBatchAssetId = Brand<string, "BrollBatchAssetId">
export type AssetJobId = Brand<string, "AssetJobId">
export type AvatarId = Brand<string, "AvatarId">
export type AvatarBatchId = Brand<string, "AvatarBatchId">
export type VoiceId = Brand<string, "VoiceId">
export type SceneId = Brand<string, "SceneId">
export type AccountId = Brand<string, "AccountId">
export type SessionId = Brand<string, "SessionId">
export type UserTeamRelationId = Brand<string, "UserTeamRelationId">
export type TeamInviteId = Brand<string, "TeamInviteId">
export type UsageLogId = Brand<string, "UsageLogId">
export type DeletionRequestId = Brand<string, "DeletionRequestId">
export type AccessLogId = Brand<string, "AccessLogId">
export type UploadedAudioId = Brand<string, "UploadedAudioId">
export type ApiKeyId = Brand<string, "ApiKeyId">
export type BackgroundMusicId = Brand<string, "BackgroundMusicId">
export type ShareableLinkId = Brand<string, "ShareableLinkId">
export type ShareableLinkToken = Brand<string, "ShareableLinkToken">
export type AuditLogId = Brand<string, "AuditLogId">
export type BrandId = Brand<string, "BrandId">
export type ChatMessageId = Brand<string, "ChatMessageId">
export type TeamContentSeedId = Brand<string, "TeamContentSeedId">
export type ContentSeedId = Brand<string, "ContentSeedId">
export type WebhookEndpointId = Brand<string, "WebhookEndpointId">
export type WebhookDeliveryId = Brand<string, "WebhookDeliveryId">
export type PromptTemplateId = Brand<string, "PromptTemplateId">
export type AvatarModelId = Brand<string, "AvatarModelId">
export type ContentBrandId = Brand<string, "ContentBrandId">
export type ContentSourceId = Brand<string, "ContentSourceId">
export type ScrapedContentId = Brand<string, "ScrapedContentId">
export type SwipeBoardId = Brand<string, "SwipeBoardId">
export type SwipeBoardItemId = Brand<string, "SwipeBoardItemId">
export type TagId = Brand<string, "TagId">
export type ScrapedContentFavoriteId = Brand<string, "ScrapedContentFavoriteId">
export type NotificationId = Brand<string, "NotificationId">
export type EditorVersionId = Brand<string, "EditorVersionId">
export type StoryboardSnapshotId = Brand<string, "StoryboardSnapshotId">
export type WorkflowId = Brand<string, "WorkflowId">
export type WorkflowExecutionId = Brand<string, "WorkflowExecutionId">
export type WorkflowReviewItemId = Brand<string, "WorkflowReviewItemId">
export type ScratchId = Brand<string, "ScratchId">
export type MediaSpaceBoardId = Brand<string, "MediaSpaceBoardId">
export type MediaSpaceBoardAssetId = Brand<string, "MediaSpaceBoardAssetId">
export type AssetCommentId = Brand<string, "AssetCommentId">
export type MediaSpaceBoardColumnId = Brand<string, "MediaSpaceBoardColumnId">
export type MediaSpaceAutomationId = Brand<string, "MediaSpaceAutomationId">
export type StaticAdGenerationId = Brand<string, "StaticAdGenerationId">
export type ImageEditId = Brand<string, "ImageEditId">
export type ImageEditVersionId = Brand<string, "ImageEditVersionId">
export type ImageCarouselId = Brand<string, "ImageCarouselId">
export type ImageCarouselVersionId = Brand<string, "ImageCarouselVersionId">

export const asVideoId = (id: string): VideoId => id as VideoId
export const asSeriesId = (id: string): SeriesId => id as SeriesId
export const asAssetId = (id: string): AssetId => id as AssetId
export const asAudioAssetId = (id: string): AudioAssetId => id as AudioAssetId
export const asFeedbackId = (id: string): FeedbackId => id as FeedbackId
export const asPostedMediaId = (id: string): PostedMediaId => id as PostedMediaId
export const asPostedMediaMetricSnapshotId = (id: string): PostedMediaMetricSnapshotId =>
  id as PostedMediaMetricSnapshotId
export const asPostedMediaSyncRunId = (id: string): PostedMediaSyncRunId => id as PostedMediaSyncRunId
export const asScheduledPostId = (id: string): ScheduledPostId => id as ScheduledPostId
export const asProductId = (id: string): ProductId => id as ProductId
export const asAlbumId = (id: string): AlbumId => id as AlbumId
export const asBrollBatchId = (id: string): BrollBatchId => id as BrollBatchId
export const asBrollBatchAssetId = (id: string): BrollBatchAssetId => id as BrollBatchAssetId
export const asAssetJobId = (id: string): AssetJobId => id as AssetJobId
export const asAvatarId = (id: string): AvatarId => id as AvatarId
export const asAvatarBatchId = (id: string): AvatarBatchId => id as AvatarBatchId
export const asVoiceId = (id: string): VoiceId => id as VoiceId
export const asSceneId = (id: string): SceneId => id as SceneId
export const asAccountId = (id: string): AccountId => id as AccountId
export const asSessionId = (id: string): SessionId => id as SessionId
export const asUserTeamRelationId = (id: string): UserTeamRelationId => id as UserTeamRelationId
export const asTeamInviteId = (id: string): TeamInviteId => id as TeamInviteId
export const asUsageLogId = (id: string): UsageLogId => id as UsageLogId
export const asDeletionRequestId = (id: string): DeletionRequestId => id as DeletionRequestId
export const asAccessLogId = (id: string): AccessLogId => id as AccessLogId
export const asUploadedAudioId = (id: string): UploadedAudioId => id as UploadedAudioId
export const asApiKeyId = (id: string): ApiKeyId => id as ApiKeyId
export const asBackgroundMusicId = (id: string): BackgroundMusicId => id as BackgroundMusicId
export const asShareableLinkId = (id: string): ShareableLinkId => id as ShareableLinkId
export const asShareableLinkToken = (id: string): ShareableLinkToken => id as ShareableLinkToken
export const asAuditLogId = (id: string): AuditLogId => id as AuditLogId
export const asBrandId = (id: string): BrandId => id as BrandId
export const asChatMessageId = (id: string): ChatMessageId => id as ChatMessageId
export const asTeamContentSeedId = (id: string): TeamContentSeedId => id as TeamContentSeedId
export const asContentSeedId = (id: string): ContentSeedId => id as ContentSeedId
export const asWebhookEndpointId = (id: string): WebhookEndpointId => id as WebhookEndpointId
export const asWebhookDeliveryId = (id: string): WebhookDeliveryId => id as WebhookDeliveryId
export const asPromptTemplateId = (id: string): PromptTemplateId => id as PromptTemplateId
export const asAvatarModelId = (id: string): AvatarModelId => id as AvatarModelId
export const asContentBrandId = (id: string): ContentBrandId => id as ContentBrandId
export const asContentSourceId = (id: string): ContentSourceId => id as ContentSourceId
export const asScrapedContentId = (id: string): ScrapedContentId => id as ScrapedContentId
export const asSwipeBoardId = (id: string): SwipeBoardId => id as SwipeBoardId
export const asSwipeBoardItemId = (id: string): SwipeBoardItemId => id as SwipeBoardItemId
export const asTagId = (id: string): TagId => id as TagId
export const asScrapedContentFavoriteId = (id: string): ScrapedContentFavoriteId => id as ScrapedContentFavoriteId
export const asNotificationId = (id: string): NotificationId => id as NotificationId
export const asEditorVersionId = (id: string): EditorVersionId => id as EditorVersionId
export const asStoryboardSnapshotId = (id: string): StoryboardSnapshotId => id as StoryboardSnapshotId
export const asWorkflowId = (id: string): WorkflowId => id as WorkflowId
export const asWorkflowExecutionId = (id: string): WorkflowExecutionId => id as WorkflowExecutionId
export const asWorkflowReviewItemId = (id: string): WorkflowReviewItemId => id as WorkflowReviewItemId
export const asScratchId = (id: string): ScratchId => id as ScratchId
export const asMediaSpaceBoardId = (id: string): MediaSpaceBoardId => id as MediaSpaceBoardId
export const asMediaSpaceBoardAssetId = (id: string): MediaSpaceBoardAssetId => id as MediaSpaceBoardAssetId
export const asAssetCommentId = (id: string): AssetCommentId => id as AssetCommentId
export const asMediaSpaceBoardColumnId = (id: string): MediaSpaceBoardColumnId => id as MediaSpaceBoardColumnId
export const asMediaSpaceAutomationId = (id: string): MediaSpaceAutomationId => id as MediaSpaceAutomationId
export const asStaticAdGenerationId = (id: string): StaticAdGenerationId => id as StaticAdGenerationId
export const asImageEditId = (id: string): ImageEditId => id as ImageEditId
export const asImageEditVersionId = (id: string): ImageEditVersionId => id as ImageEditVersionId
export const asImageCarouselId = (id: string): ImageCarouselId => id as ImageCarouselId
export const asImageCarouselVersionId = (id: string): ImageCarouselVersionId => id as ImageCarouselVersionId
export const assetJobIdFromBoundary = (id: string): AssetJobId => asAssetJobId(id)
export const sceneIdFromGeneratedId = (id: string): SceneId => asSceneId(id)

export const zVideoId = z.string().min(1).transform(asVideoId)
export const zSeriesId = z.string().min(1).transform(asSeriesId)
export const zAssetId = z.string().min(1).transform(asAssetId)
export const zAudioAssetId = z.string().min(1).transform(asAudioAssetId)
export const zFeedbackId = z.string().min(1).transform(asFeedbackId)
export const zPostedMediaId = z.string().min(1).transform(asPostedMediaId)
export const zPostedMediaMetricSnapshotId = z.string().min(1).transform(asPostedMediaMetricSnapshotId)
export const zPostedMediaSyncRunId = z.string().min(1).transform(asPostedMediaSyncRunId)
export const zScheduledPostId = z.string().min(1).transform(asScheduledPostId)
export const zProductId = z.string().min(1).transform(asProductId)
export const zAlbumId = z.string().min(1).transform(asAlbumId)
export const zBrollBatchId = z.string().min(1).transform(asBrollBatchId)
export const zBrollBatchAssetId = z.string().min(1).transform(asBrollBatchAssetId)
export const zAssetJobId = z.string().min(1).transform(asAssetJobId)
export const zAvatarId = z.string().min(1).transform(asAvatarId)
export const zAvatarBatchId = z.string().min(1).transform(asAvatarBatchId)
export const zVoiceId = z.string().min(1).transform(asVoiceId)
export const zSceneId = z.string().min(1).transform(asSceneId)
export const zAccountId = z.string().min(1).transform(asAccountId)
export const zSessionId = z.string().min(1).transform(asSessionId)
export const zUserTeamRelationId = z.string().min(1).transform(asUserTeamRelationId)
export const zTeamInviteId = z.string().min(1).transform(asTeamInviteId)
export const zUsageLogId = z.string().min(1).transform(asUsageLogId)
export const zDeletionRequestId = z.string().min(1).transform(asDeletionRequestId)
export const zAccessLogId = z.string().min(1).transform(asAccessLogId)
export const zUploadedAudioId = z.string().min(1).transform(asUploadedAudioId)
export const zApiKeyId = z.string().min(1).transform(asApiKeyId)
export const zBackgroundMusicId = z.string().min(1).transform(asBackgroundMusicId)
export const zShareableLinkId = z.string().min(1).transform(asShareableLinkId)
export const zShareableLinkToken = z
  .string()
  .regex(/^[a-zA-Z0-9]{8}$/)
  .transform(asShareableLinkToken)
export const zAuditLogId = z.string().min(1).transform(asAuditLogId)
export const zBrandId = z.string().min(1).transform(asBrandId)
export const zChatMessageId = z.string().min(1).transform(asChatMessageId)
export const zTeamContentSeedId = z.string().min(1).transform(asTeamContentSeedId)
export const zContentSeedId = z.string().min(1).transform(asContentSeedId)
export const zWebhookEndpointId = z.string().min(1).transform(asWebhookEndpointId)
export const zWebhookDeliveryId = z.string().min(1).transform(asWebhookDeliveryId)
export const zPromptTemplateId = z.string().min(1).transform(asPromptTemplateId)
export const zAvatarModelId = z.string().min(1).transform(asAvatarModelId)
export const zContentBrandId = z.string().min(1).transform(asContentBrandId)
export const zContentSourceId = z.string().min(1).transform(asContentSourceId)
export const zScrapedContentId = z.string().min(1).transform(asScrapedContentId)
export const zSwipeBoardId = z.string().min(1).transform(asSwipeBoardId)
export const zSwipeBoardItemId = z.string().min(1).transform(asSwipeBoardItemId)
export const zTagId = z.string().min(1).transform(asTagId)
export const zScrapedContentFavoriteId = z.string().min(1).transform(asScrapedContentFavoriteId)
export const zNotificationId = z.string().min(1).transform(asNotificationId)
export const zEditorVersionId = z.string().min(1).transform(asEditorVersionId)
export const zStoryboardSnapshotId = z.string().min(1).transform(asStoryboardSnapshotId)
export const zWorkflowId = z.string().min(1).transform(asWorkflowId)
export const zWorkflowExecutionId = z.string().min(1).transform(asWorkflowExecutionId)
export const zWorkflowReviewItemId = z.string().min(1).transform(asWorkflowReviewItemId)
export const zScratchId = z.string().min(1).transform(asScratchId)
export const zMediaSpaceBoardId = z.string().min(1).transform(asMediaSpaceBoardId)
export const zMediaSpaceBoardAssetId = z.string().min(1).transform(asMediaSpaceBoardAssetId)
export const zAssetCommentId = z.string().min(1).transform(asAssetCommentId)
export const zMediaSpaceBoardColumnId = z.string().min(1).transform(asMediaSpaceBoardColumnId)
export const zMediaSpaceAutomationId = z.string().min(1).transform(asMediaSpaceAutomationId)
export const zStaticAdGenerationId = z.string().min(1).transform(asStaticAdGenerationId)
export const zImageEditId = z.string().min(1).transform(asImageEditId)
export const zImageEditVersionId = z.string().min(1).transform(asImageEditVersionId)
export const zImageCarouselId = z.string().min(1).transform(asImageCarouselId)
export const zImageCarouselVersionId = z.string().min(1).transform(asImageCarouselVersionId)

// ---------------------------------------------------------------------------
// #3 Secrets / token trust levels — confusing these = secret leak or auth bug
// ---------------------------------------------------------------------------

/** Raw `vm_live_*` key as supplied by the client. Sensitive — never log. */
export type PlaintextApiKey = Brand<string, "PlaintextApiKey">
/** SHA-256 hash of a PlaintextApiKey — what we store + index in the DB. Safe to log. */
export type HashedApiKey = Brand<string, "HashedApiKey">
/** A signed, self-verifying JWT. */
export type SignedJWT = Brand<string, "SignedJWT">
/** An opaque session id whose trust comes from a Redis/DB lookup. Sensitive — never log. */
export type SessionToken = Brand<string, "SessionToken">

export const asPlaintextApiKey = (key: string): PlaintextApiKey => key as PlaintextApiKey
export const asHashedApiKey = (hash: string): HashedApiKey => hash as HashedApiKey
export const asSignedJwt = (token: string): SignedJWT => token as SignedJWT
export const asSessionToken = (token: string): SessionToken => token as SessionToken

export const zPlaintextApiKey = z.string().transform(asPlaintextApiKey)
export const zHashedApiKey = z.string().transform(asHashedApiKey)
export const zSignedJwt = z.string().transform(asSignedJwt)
export const zSessionToken = z.string().transform(asSessionToken)

// ---------------------------------------------------------------------------
// #4 Money — Stripe amounts are in CENTS; display/analytics is in DOLLARS
// ---------------------------------------------------------------------------

export type Cents = Brand<number, "Cents">
export type UsdDollars = Brand<number, "UsdDollars">

export const asCents = (n: number): Cents => n as Cents
export const asDollars = (n: number): UsdDollars => n as UsdDollars

export const zCents = z.number().transform(asCents)
export const zDollars = z.number().transform(asDollars)

/** The single place the `/ 100` conversion lives. */
export const centsToDollars = (cents: Cents): UsdDollars => ((cents as number) / 100) as unknown as UsdDollars
export const dollarsToCents = (dollars: UsdDollars): Cents => Math.round((dollars as number) * 100) as unknown as Cents

// ---------------------------------------------------------------------------
// #5 Epoch time — Stripe returns SECONDS; JS Date.now() is MILLISECONDS (1000×)
// ---------------------------------------------------------------------------

export type EpochSeconds = Brand<number, "EpochSeconds">
export type EpochMillis = Brand<number, "EpochMillis">

export const asEpochSeconds = (n: number): EpochSeconds => n as EpochSeconds
export const asEpochMillis = (n: number): EpochMillis => n as EpochMillis

export const zEpochSeconds = z.number().transform(asEpochSeconds)
export const zEpochMillis = z.number().transform(asEpochMillis)

export const epochSecondsToDate = (s: EpochSeconds): Date => new Date((s as number) * 1000)
export const dateToEpochSeconds = (d: Date): EpochSeconds => Math.floor(d.getTime() / 1000) as unknown as EpochSeconds
export const epochMillisToSeconds = (ms: EpochMillis): EpochSeconds =>
  Math.floor((ms as number) / 1000) as unknown as EpochSeconds
/** Current time as epoch SECONDS (the unit Stripe + FB CAPI expect). */
export const nowEpochSeconds = (): EpochSeconds => Math.floor(Date.now() / 1000) as unknown as EpochSeconds

// ---------------------------------------------------------------------------
// #6 Credits — a BALANCE (what a team has) vs a COST (what an action charges)
// ---------------------------------------------------------------------------

export type CreditBalance = Brand<number, "CreditBalance">
export type CreditCost = Brand<number, "CreditCost">

export const asCreditBalance = (n: number): CreditBalance => n as CreditBalance
export const asCreditCost = (n: number): CreditCost => n as CreditCost

export const zCreditBalance = z.number().transform(asCreditBalance)
export const zCreditCost = z.number().transform(asCreditCost)

export const hasSufficientCredits = (balance: CreditBalance, cost: CreditCost): boolean =>
  (balance as number) >= (cost as number)
/** Apply a cost to a balance, clamped at 0. Returns the new balance. */
export const deductCredits = (balance: CreditBalance, cost: CreditCost): CreditBalance =>
  Math.max(0, (balance as number) - (cost as number)) as unknown as CreditBalance

// ---------------------------------------------------------------------------
// Duration units — a video's LENGTH (seconds) vs elapsed/execution time (millis)
// vs a frame count. A 5-second request sent as 5000 (ms) is the classic mixup.
// ---------------------------------------------------------------------------

export type DurationSeconds = Brand<number, "DurationSeconds">
export type DurationMs = Brand<number, "DurationMs">
export type FrameCount = Brand<number, "FrameCount">

export const asDurationSeconds = (n: number): DurationSeconds => n as DurationSeconds
export const asDurationMs = (n: number): DurationMs => n as DurationMs
export const asFrameCount = (n: number): FrameCount => n as FrameCount

export const zDurationSeconds = z.number().transform(asDurationSeconds)
export const zDurationMs = z.number().transform(asDurationMs)
export const zFrameCount = z.number().transform(asFrameCount)
